import React, { FC, ReactNode, useMemo } from 'react';
import { usePhone, useTablet } from '@clds/style-utils';
import Tooltip from '@clds/tooltip';
import useHover from '@cld/use-hover';
import { useImgWithFallback } from '@cld/use-img-with-fallback';
import { CookbookTransformation } from '../../types';
import { SearchOption } from '../SearchField';
import { CookbookCardLayout } from './CookbookTransformationCardLayout';
import { RecipeExcerpt } from './RecipeExcerpt';
import { ExcerptSnippet } from './ExcerptSnippet/ExcerptSnippet';
import { Header, RowLayout, Thumbnail } from './CookbookTransformationCard.styled';
import { Pills } from './Pills';

export interface CookbookTransformationCardProps {
  title: CookbookTransformation['title'];
  excerpt: CookbookTransformation['excerpt'];
  complexity: CookbookTransformation['complexity'];
  content?: CookbookTransformation['content'];
  categories: CookbookTransformation['categories'];
  thumbnailUrl: CookbookTransformation['thumbnailUrl'];
  id?: CookbookTransformation['id'];
  onQueryChipClick: (option: SearchOption) => void;
  forceMobileLayout?: boolean;
  hoverProps: ReturnType<typeof useHover>;
  footerPanel: ReactNode;
}

export interface CookbookTransformationCardComposition {
  RowLayout: typeof RowLayout;
}

export const CookbookTransformationCard: FC<React.PropsWithChildren<CookbookTransformationCardProps>> & CookbookTransformationCardComposition = ({
  excerpt,
  title,
  onQueryChipClick,
  categories,
  complexity,
  thumbnailUrl,
  forceMobileLayout,
  footerPanel,
  hoverProps,
}) => {
  const isPhone = usePhone() || forceMobileLayout;
  const isTablet = useTablet() && !forceMobileLayout;
  const isMobile = isPhone || isTablet;
  const uniqueTags = useMemo(() => [...new Set(categories)], [categories]);
  const { imgProps } = useImgWithFallback(thumbnailUrl);
  const details = (
    <>
      <Header>
        <Tooltip title={title}>
          <Tooltip.OverflowTrigger>{title}</Tooltip.OverflowTrigger>
        </Tooltip>
        {!isMobile && <Pills tags={uniqueTags} complexity={complexity} onQueryChipClick={onQueryChipClick} isMobile={false} />}
      </Header>

      <RecipeExcerpt>{excerpt}</RecipeExcerpt>

      <ExcerptSnippet excerpt={excerpt} />

      {footerPanel}
    </>
  );

  return (
    <CookbookCardLayout
      {...hoverProps}
      leftContent={
        <>
          <Thumbnail loading="lazy" {...imgProps} alt="thumbnail" />
          {isMobile && <Pills tags={uniqueTags} complexity={complexity} onQueryChipClick={onQueryChipClick} isMobile />}
          {isPhone && details}
        </>
      }
      rightContent={isPhone ? undefined : details}
    />
  );
};

CookbookTransformationCard.RowLayout = RowLayout;
